import { useNavigate, useParams } from 'react-router-dom';
import type { LoginInput, LoginOption } from '../../domain/entities/auth.entity';
import { authContainer } from '../../di/container';
import { useInstitutionStore } from '../store/institution.store';

// Orquestra login/troca de turma em cima do estado compartilhado do
// institution.store — precisa ser compartilhado porque quem dispara a troca
// (Header) e quem exibe as opções (LoginModal) são componentes diferentes,
// cada um com sua própria instância deste hook.
export function useLogin() {
  const {
    setInstitution,
    setCourseId,
    setShowLoginModal,
    loginOptions: options,
    setLoginOptions,
    pendingToken,
    setPendingToken,
    isSwitchingList: isSwitching,
    setIsSwitchingList,
    authLoading: isLoading,
    setAuthLoading,
    authError: error,
    setAuthError,
  } = useInstitutionStore();
  const navigate = useNavigate();
  const { institution: urlInstitution } = useParams<{ institution: string }>();

  // `login` sempre devolve `ambiguous` quando as credenciais são válidas — a
  // confirmação final (mesmo com 1 opção só) acontece em `selectOption`.
  const login = async (input: LoginInput) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const { token, result } = await authContainer.repository.login(input);
      if (result.status === 'ambiguous') {
        setPendingToken(token);
        setLoginOptions(result.options);
      } else {
        setAuthError('Credenciais inválidas.');
      }
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Falha ao autenticar');
    } finally {
      setAuthLoading(false);
    }
  };

  // Reabre a tela de seleção reaproveitando a sessão já válida — não pede
  // e-mail/senha de novo. Usado no botão "Trocar turma".
  const openSwitcher = async (currentInstitution: string) => {
    const token = authContainer.repository.getToken(currentInstitution);
    if (!token) return;

    setIsSwitchingList(true);
    setAuthLoading(true);
    setAuthError(null);
    setShowLoginModal(true);
    try {
      const { token: freshToken, result } = await authContainer.repository.reverify(token);
      if (result.status === 'ambiguous') {
        setPendingToken(freshToken);
        setLoginOptions(result.options);
      } else {
        setAuthError('Não foi possível carregar suas turmas. Faça login novamente.');
      }
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Falha ao carregar turmas');
    } finally {
      setAuthLoading(false);
    }
  };

  const selectOption = async (option: LoginOption) => {
    if (!pendingToken) return;
    setAuthLoading(true);
    setAuthError(null);
    try {
      const result = await authContainer.repository.selectOption(pendingToken, option);
      if (result.status === 'success') {
        setInstitution(option.institution);
        setCourseId(authContainer.repository.getCourseId(option.institution));
        setLoginOptions(null);
        setPendingToken(null);
        setIsSwitchingList(false);
        setShowLoginModal(false);

        // A sessão fica salva sob a institution escolhida — se for diferente
        // da URL atual (ex.: escolheu ARISTO estando em /FAG), navega pra lá,
        // senão um refresh não acha a sessão e pede login de novo.
        if (option.institution.toUpperCase() !== urlInstitution?.toUpperCase()) {
          navigate(`/${option.institution}`, { replace: true });
        }
      } else {
        setAuthError('Não foi possível confirmar essa lista. Tente novamente.');
      }
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Falha ao autenticar');
    } finally {
      setAuthLoading(false);
    }
  };

  // No login inicial, "Voltar" só limpa a seleção (volta pro formulário). Já
  // trocando de turma a sessão continua válida, então fecha o modal direto.
  const cancelSelection = () => {
    setLoginOptions(null);
    setPendingToken(null);
    setAuthError(null);
    if (isSwitching) {
      setIsSwitchingList(false);
      setShowLoginModal(false);
    }
  };

  return {
    login,
    openSwitcher,
    selectOption,
    cancelSelection,
    options,
    isSwitching,
    isLoading,
    error,
  };
}
